import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  Output,
  EventEmitter
} from '@angular/core';
import { Idrug } from '../../interfaces/drug.interface';
import { Observable } from 'rxjs/Observable';
import { TherapiesActionCreatorService } from '../../services/therapies-action-creator.service';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { usersTypes } from '@ispm/user/constants/usersTypes.enum';
import {
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  takeWhile,
  tap
} from 'rxjs/operators';
import { IListField } from '@ispm/shared/interfaces/list-field.interface';
import { ButtonAction } from '@ispm/shared/classes/button-action.class';
import { get } from 'lodash';

@Component({
  selector: 'gen-therapies-selection',
  templateUrl: './therapies-selection.component.html',
  styleUrls: ['./therapies-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TherapiesSelectionComponent implements OnDestroy {
  private _count: number = 0;

  @Input()
  pathToList: string;

  @Input()
  textPath: string = null;

  @Input()
  textEditable?: boolean = true;

  @Input()
  set count(amount: number) {
    this._count = amount;
    this.automationClass = `Auto_Reviewers_list_${this.count}`;
  }

  get count() {
    return this._count;
  }

  @Input()
  revisionId: string;

  @Input()
  workflowId: string;

  @Input()
  edit: boolean = false;

  @Input()
  showPractitionerLayout: boolean = true;

  @Input()
  collapsed: boolean = false;

  @Input()
  displayActionButtons: boolean;

  @Input()
  priorityAvailable: boolean;

  private _review;
  @Input()
  set review(review: any) {
    this._review = review;
    this.setReview();
  }

  get review() {
    return this._review;
  }

  @Input() list: Observable<Idrug[]> = Observable.of([]);
  private _signoffAllowed: boolean = false;
  @Input()
  set signoffAllowed(signoffAllowed: boolean) {
    this._signoffAllowed = signoffAllowed;
  }

  get signoffAllowed() {
    return this._signoffAllowed;
  }

  @Input() allowManual: boolean = true;
  @Input() allowTherapyFreeText: boolean = false;
  @Input() targetMaxLength: number = 30;
  @Input() EnableAddingTrials: boolean = false;

  @Input()
  public fields: Array<IListField>;

  @Output()
  public inprogressUpdate: EventEmitter<boolean> = new EventEmitter<boolean>();
  private _inprogress = false;
  set inprogress(inprogress: boolean) {
    this._inprogress = inprogress;
    this.inprogressUpdate.emit(this._inprogress);
  }

  get inprogress() {
    return this._inprogress;
  }

  private sent: boolean = false;
  private destroyer$: Subject<any> = new Subject();
  private modalSubscriber$: Subscription;
  private destroyed: boolean = false;
  private userReview$: any;
  public selectedLength: number;
  public minSelection: number = 0;
  public maxSelection: number = -1;
  public countText: string;
  public practitionerID: string;
  public setDate: any;
  public rationale: string;
  public returnedTherapies: Observable<any>;
  public userType: usersTypes = usersTypes.PRACTITIONER;
  public automationClass: string;
  public buttonActions: Array<ButtonAction> = [];
  public secondaryButtonActions: Array<ButtonAction> = [];


  constructor(
    private therapiesAC: TherapiesActionCreatorService,
    private changeDetector: ChangeDetectorRef,
  ) {
    this.userReview$ = this.therapiesAC.getState('userReview')
      .pipe(
        takeUntil(this.destroyer$.asObservable()),
        filter(userReview => !!userReview)
      );
  }

  setReview() {
    if (this.edit) {
      this.list = this.userReview$
        .pipe(
          map((userReview: any) => userReview.priority.asMutable().toArray()),
          tap((priorityArray: any[]) => {
            this.selectedLength = priorityArray.length;
          }),
          map((reviewList: Array<any>) => {
            if (this.pathToList) {
              const listArray = reviewList.map(list => get(list, this.pathToList, []));
              return listArray.length > 0 ? listArray[0] : listArray;
            }
            return reviewList;
          }),
          tap(() => {
            this.defineRationale();
          }),
          distinctUntilChanged()
        );

      this.returnedTherapies = this.therapiesAC.getState('therapies')
        .map(items => items.map(item => new Object({ gene: item['gene']['name'], aberration: item['alteration']})));

      this.defineRationale();
    } else {
      this.initReviewData();
    }
  }

  private defineRationale() {
    this.rationale = '';
    this.userReview$
      .pipe(
        takeWhile(() => !this.rationale),
        map((userReview: any) => {
          return this.setRationaleFromState(userReview);
        }),
        filter(rationale => !!rationale)
      )
      .subscribe(rationale => {
        this.rationale = rationale;
        if (!this.destroyed) {
          this.changeDetector.detectChanges();
        }
      });
  }

  initReviewData() {
    this.setCountText();
    this.list = Observable.of(this.review.drugs);
    this.setRationale();
  }

  setRationale() {
    this.rationale = this.setRationaleFromState(this.review);
    this.setDate = this.review.meta.lastUpdated;
    this.practitionerID = this.review.context.find(item => item.resourceType.toLowerCase() === 'practitioner').reference;
  }

  setRationaleFromState(userReview): string {
    if (this.textPath) {
      return get(userReview.priority.toArray()[0], this.textPath, '');
    } else {
      return get(userReview, 'rationale', '');
    }
  }

  setCountText() {
    switch (this.count) {
      case 1:
        this.countText = '1st';
        break;
      case 2:
        this.countText = '2nd';
        break;
      case 3:
        this.countText = '3rd';
        break;
      default:
        this.countText = `${this.count}th`;
        break;
    }
  }

  therapyListType(): string {
    return (this.allowManual && this.edit) ? 'manual-add-list' : '';
  }

  removeTherapy(therapy) {
    this.therapiesAC.removeTherapyFromPriority(therapy);
  }

  addNewTherapy(data) { //In use for therpies and trials
    this.therapiesAC.setTherapyInPriority(data);
  }

  updateListOrder(list) {
    this.therapiesAC.setPriorityList(list);
  }

  updateRationale(rationale: string) {
    this.therapiesAC.updateRationale(rationale);
  }

  ngOnDestroy(): void {
    if (this.modalSubscriber$) {
      this.modalSubscriber$.unsubscribe();
    }

    this.destroyer$.next();
    this.destroyer$.complete();
    this.destroyed = true;
  }
}
